import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import LanguageIcon from '@mui/icons-material/Language'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Cookie from 'js-cookie'
import { useAppSelector } from '~/store'
import { languageLabels } from '../../constants'

type Language = keyof typeof languageLabels

export default function LanguageButton() {
    const csrftoken = Cookie.get('csrftoken')

    const setLanguageURL = useAppSelector((state) => state.initial.endpoints.setLanguageURL)
    const { i18n } = useTranslation()
    const currentLanguage = i18n.language as Language
    const languages = useMemo(
        () =>
            Object.keys(languageLabels).sort((a, b) => {
                if (a === currentLanguage) {
                    return -1
                }

                if (b === currentLanguage) {
                    return 1
                }

                return 0
            }) as Language[],
        [currentLanguage]
    )

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const open = !!anchorEl

    const languageInputRef = useRef<HTMLInputElement>(null)
    function handleSelectLanguage(language: string) {
        if (languageInputRef.current) {
            languageInputRef.current.value = language
            languageInputRef.current.form?.submit()
        }
    }

    return (
        <>
            <IconButton
                aria-owns={open ? 'menu-appbar' : undefined}
                aria-haspopup="true"
                color="inherit"
                onClick={(event) => setAnchorEl(event.currentTarget)}
            >
                <LanguageIcon />
            </IconButton>
            <Menu
                id="menu-appbar"
                open={!!anchorEl}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                onClose={() => setAnchorEl(null)}
            >
                {languages.map((l) => (
                    <MenuItem
                        key={l}
                        selected={l === currentLanguage}
                        disabled={l === currentLanguage}
                        onClick={() => handleSelectLanguage(l)}
                    >
                        {languageLabels[l]}
                    </MenuItem>
                ))}
            </Menu>
            <form action={setLanguageURL} method="post">
                <input type="hidden" name="language" ref={languageInputRef} />
                <input type="hidden" name="next" value={window.location.href} />
                <input type="hidden" name="csrfmiddlewaretoken" value={csrftoken} />
            </form>
        </>
    )
}
